/**
 * Перетаскивание карточки каталога на чертёж или в 3D (drag & drop).
 *
 * Нажал на карточку и повёл — как только курсор сдвинулся на несколько пикселей, тип
 * выбирается так же, как кликом (`startCatalogDrag`): за курсором по холсту едет призрак,
 * а рядом с курсором — плашка с названием. Отпустил над холстом — холст сам ставит объект
 * туда, где призрак, и заканчивает перетаскивание. Отпустил мимо (на панели, за окном)
 * или нажал Esc — установка отменяется, ничего не ставится.
 *
 * Перетаскивание сделано на событиях указателя, а не на HTML5 drag & drop: при нём холст
 * не получает обычных pointermove, и призрак не поедет за курсором. Пока кнопка зажата,
 * события мыши идут элементу под курсором — холсту, — поэтому призрак работает как при клике.
 *
 * Пальцем: просто повёл по списку — список прокручивается; задержал палец на карточке
 * (`LONG_PRESS_MS`) — карточка «берётся», и её тащат так же, как мышью. Касание браузер
 * сам привязывает к элементу, где оно началось, поэтому, взяв карточку, захват с неё
 * снимаем — дальше события получает то, что под пальцем: холст или сцена. Пока карточку
 * тащат, прокрутку панели гасим.
 *
 * Клик без сдвига по-прежнему просто выбирает тип; клик, который приходит сразу после
 * перетаскивания (отпустили над той же карточкой), гасится.
 */
import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import type { CatalogRef } from "@/shared/plan";
import { useEditorStore } from "@/widgets/editor";

/** Сдвиг больше этого, пикселей, — уже перетаскивание (мышь) или прокрутка списка (палец). */
const DRAG_SLOP = 6;
/** Сколько после перетаскивания гасить клик по карточке, мс. */
const CLICK_GUARD_MS = 300;
/** Сколько держать палец на карточке, чтобы её взять, мс. */
const LONG_PRESS_MS = 300;

/** Плашка у курсора: что тащат и где курсор. */
export interface CatalogDragState {
	title: string;
	x: number;
	y: number;
}

/** Обработчики карточки, которую можно тащить. */
export interface CatalogCardDragProps {
	onPointerDown: (event: PointerEvent<HTMLElement>) => void;
	onClickCapture: (event: MouseEvent<HTMLElement>) => void;
	onDragStart: (event: MouseEvent<HTMLElement>) => void;
	onContextMenu: (event: MouseEvent<HTMLElement>) => void;
}

/** Нажатие на карточку, из которого может вырасти перетаскивание. */
interface Press {
	/** Начать перетаскивание: выбрать тип каталога или инструмент проёма */
	start: () => void;
	title: string;
	x: number;
	y: number;
	pointerId: number;
	/** Карточка, на которой началось касание: с неё снимаем захват */
	card: HTMLElement;
	/** Пальцем карточку берут долгим нажатием; мышью — сразу */
	touch: boolean;
	/** Палец задержался — карточка взята, её тащат */
	held: boolean;
}

/** Идёт ли перетаскивание карточки — из каталога или из «Проёмов». */
const isDragging = () => {
	const editor = useEditorStore.getState();
	return editor.catalogDrag !== null || editor.openingDrag !== null;
};

/** Отпустили мимо холста или нажали Esc — установка отменяется. */
const cancelDrag = () => {
	const editor = useEditorStore.getState();
	if (editor.catalogDrag) editor.endCatalogDrag(true);
	if (editor.openingDrag) editor.endOpeningDrag(true);
};

export const useCatalogCardDrag = () => {
	const press = useRef<Press | null>(null);
	const longPress = useRef<number | null>(null);
	const droppedAt = useRef(0);
	const [chip, setChip] = useState<CatalogDragState | null>(null);

	useEffect(() => {
		const cancelLongPress = () => {
			if (longPress.current !== null) window.clearTimeout(longPress.current);
			longPress.current = null;
		};
		const move = (event: globalThis.PointerEvent) => {
			const current = press.current;
			if (!current || event.pointerId !== current.pointerId) return;
			const moved = Math.hypot(event.clientX - current.x, event.clientY - current.y);
			if (current.touch && !current.held) {
				// Палец повёл, не задержавшись, — это прокрутка списка, а не перетаскивание
				if (moved >= DRAG_SLOP) {
					cancelLongPress();
					press.current = null;
				}
				return;
			}
			if (!isDragging()) {
				if (moved < DRAG_SLOP) return;
				current.start();
			}
			setChip({ title: current.title, x: event.clientX, y: event.clientY });
		};
		// Холст ловит отпускание раньше окна и сам ставит объект; дошло сюда с незакрытым
		// перетаскиванием — отпустили мимо холста
		const finish = () => {
			cancelLongPress();
			if (!press.current) return;
			press.current = null;
			setChip(null);
			if (!isDragging()) return;
			droppedAt.current = performance.now();
			cancelDrag();
		};
		// Взятую пальцем карточку тащат — панель под ней не прокручиваем
		const touchMove = (event: TouchEvent) => {
			if (press.current?.held) event.preventDefault();
		};
		const keyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || !press.current) return;
			cancelLongPress();
			press.current = null;
			setChip(null);
			if (isDragging()) cancelDrag();
		};
		window.addEventListener("pointermove", move);
		window.addEventListener("pointerup", finish);
		window.addEventListener("pointercancel", finish);
		window.addEventListener("touchmove", touchMove, { passive: false });
		window.addEventListener("keydown", keyDown);
		return () => {
			cancelLongPress();
			window.removeEventListener("pointermove", move);
			window.removeEventListener("pointerup", finish);
			window.removeEventListener("pointercancel", finish);
			window.removeEventListener("touchmove", touchMove);
			window.removeEventListener("keydown", keyDown);
		};
	}, []);

	/** Палец задержался на карточке: берём её и отдаём касание тому, что под пальцем. */
	const holdCard = () => {
		longPress.current = null;
		const current = press.current;
		if (!current) return;
		current.held = true;
		try {
			if (current.card.hasPointerCapture(current.pointerId)) {
				current.card.releasePointerCapture(current.pointerId);
			}
		} catch {
			// указателя уже нет — касание закончилось
		}
		current.start();
		setChip({ title: current.title, x: current.x, y: current.y });
		navigator.vibrate?.(10);
	};

	/**
	 * Обработчики для карточки, которую тащат. `start` — как начать перетаскивание (выбрать
	 * тип, включить инструмент); null — карточку не тащат.
	 */
	const dragProps = (start: (() => void) | null, title: string): CatalogCardDragProps => ({
		onPointerDown: (event: PointerEvent<HTMLElement>) => {
			if (!start || event.button !== 0 || !event.isPrimary) return;
			const touch = event.pointerType !== "mouse";
			// Мышью без этого браузер начнёт выделять текст карточек, пока тащат. Касание не
			// гасим: тогда палец не смог бы прокрутить список
			if (!touch) event.preventDefault();
			press.current = {
				start,
				title,
				x: event.clientX,
				y: event.clientY,
				pointerId: event.pointerId,
				card: event.currentTarget,
				touch,
				held: false,
			};
			if (touch) {
				if (longPress.current !== null) window.clearTimeout(longPress.current);
				longPress.current = window.setTimeout(holdCard, LONG_PRESS_MS);
			}
		},
		onClickCapture: (event: MouseEvent<HTMLElement>) => {
			if (performance.now() - droppedAt.current > CLICK_GUARD_MS) return;
			event.preventDefault();
			event.stopPropagation();
		},
		// Картинку-превью браузер иначе потащит сам, своим drag & drop
		onDragStart: (event: MouseEvent<HTMLElement>) => event.preventDefault(),
		// Долгое нажатие пальцем — это «взять карточку», а не меню браузера
		onContextMenu: (event: MouseEvent<HTMLElement>) => {
			if (press.current?.touch) event.preventDefault();
		},
	});

	/**
	 * Обработчики для карточки каталога. `ref` — что она ставит; null — карточку не тащат
	 * (пол, отделка: их назначают комнате, а не ставят).
	 */
	const cardDragProps = (ref: CatalogRef | null, title: string): CatalogCardDragProps =>
		dragProps(ref ? () => useEditorStore.getState().startCatalogDrag(ref) : null, title);

	return { cardDragProps, dragProps, chip };
};
